import {
  IconButton,
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  TableHead,
  TableRow,
  Table,
  TableContainer,
  TableCell,
  TableBody,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';
import React from 'react';
import { PaymentRequest, PaymentRequestStatus } from '../gql-types.generated';
import { paymenteStatusDisplay } from '../util/PaymentStatus';
import CloseIcon from '@material-ui/icons/Close';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    label: {
      color: theme.palette.grey[700],
    },
    value: {
      color: theme.palette.common.black,
    },
    cell: {
      color: theme.palette.grey[800],
    },
    headerText: {
      height: '100%',
      maxWidth: '85%',
    },
    headerButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
    },
    refundButton: {
      marginLeft: 8,
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
        width: '100%',
      },
    },
    dialogButtons: {
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    headerHeadText: {
      letterSpacing: 1.5,
      fontSize: 12,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(2),
      top: 2,
      color: theme.palette.grey[500],
    },
    contentHeaderText: {
      padding: theme.spacing(2, 2, 0, 2),
    },
    detailsWrapper: {
      padding: theme.spacing(2),
    },
    details: {
      padding: theme.spacing(2),
    },
    contentDivider: {
      width: 1,
    },
    communicationTable: {
      width: '100%',
    },
    communicationTableGridWrapper: {
      flexGrow: 1,
    },
  }),
);
interface PaymentRequestDetailsProps {
  open: boolean;
  onClose: () => void;
  paymentRecord?: PaymentRequest;
  remind: (paymentRequest: PaymentRequest) => void;
  refund: (paymentRequest: PaymentRequest) => void;
  defaultCurrency?: string | null;
  remindPressed: boolean;
}
const PaymentRequestDetails: React.FC<PaymentRequestDetailsProps> = props => {
  const classes = useStyles();
  const { paymentRecord, onClose, remind, refund, defaultCurrency, open, remindPressed } = props;
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <Dialog open={open} onClose={onClose} disableBackdropClick={false} disableEscapeKeyDown={false} fullWidth={true} maxWidth={'md'}>
      <>
        <DialogTitle data-cy="payment-request-details-modal">
          <Grid container spacing={1} className={classes.header}>
            <Grid item xs={12} md={8} className={classes.headerText}>
              <Typography variant="subtitle2" className={classes.headerHeadText}>
                PAYMENT REQUEST DETAILS
              </Typography>
              <Typography variant="h6">Request Reference {paymentRecord?.referenceNumber}</Typography>
              <Typography variant="body1">Created by {paymentRecord?.createdBy || 'Unknown'}</Typography>
              <IconButton
                aria-label="close"
                title="close"
                className={classes.closeButton}
                onClick={onClose}
                data-cy="pr-details-close"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <Grid item xs={12} md={3} className={matches ? '' : classes.headerButtons}>
              <Button
                className={matches ? classes.dialogButtons : ''}
                disabled={
                  !(
                    paymentRecord?.status === PaymentRequestStatus.Unpaid ||
                    paymentRecord?.status === PaymentRequestStatus.Failed ||
                    paymentRecord?.status === PaymentRequestStatus.Canceled
                  ) || remindPressed
                }
                size="medium"
                variant={'outlined'}
                aria-label={'remind'}
                color={'primary'}
                onClick={() => {
                  if (paymentRecord) remind(paymentRecord);
                }}
                data-cy="pr-details-remind"
              >
                Remind
              </Button>
              <Button
                className={classes.refundButton}
                disabled={
                  !(
                    paymentRecord?.status === PaymentRequestStatus.Completed ||
                    paymentRecord?.status === PaymentRequestStatus.PartiallyRefunded ||
                    paymentRecord?.status === PaymentRequestStatus.RefundFailed
                  )
                }
                size="medium"
                variant={'outlined'}
                aria-label={'refund'}
                color={'primary'}
                onClick={() => {
                  if (paymentRecord) refund(paymentRecord);
                }}
                data-cy="pr-details-refund"
              >
                Refund
              </Button>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" className={classes.contentHeaderText}>
                Request Details
              </Typography>
              <Grid container direction="row" spacing={1} alignItems="stretch" className={classes.details}>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Status
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {
                      paymenteStatusDisplay.find(item => {
                        return item.name === paymentRecord?.status;
                      })?.display
                    }
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Reason
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.statusReason ? paymentRecord?.statusReason : '-- --'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Date Requested
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.createdAt ? new Date(paymentRecord.createdAt as string).toLocaleDateString() : ''}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Amount
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {typeof paymentRecord?.amount === 'number'
                      ? new Intl.NumberFormat('en', {
                          style: 'currency',
                          currency: defaultCurrency || 'USD',
                          currencyDisplay: 'symbol',
                        }).format(paymentRecord.amount / 100)
                      : ''}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Attachments
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.invoiceLink && (
                      <Link href={paymentRecord.invoiceLink} data-cy="download-invoice">
                        Download
                      </Link>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={matches ? 12 : undefined} className={matches ? '' : classes.contentDivider}>
              <Divider orientation={matches ? 'horizontal' : 'vertical'} />
            </Grid>
            <Grid item xs={12} md={'auto'} className={classes.communicationTableGridWrapper}>
              <Typography variant="subtitle2" className={classes.contentHeaderText}>
                Communication History
              </Typography>
              <TableContainer className={classes.communicationTable}>
                <Table aria-label="communications">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.cell}>To</TableCell>
                      <TableCell className={classes.cell}>Type</TableCell>
                      <TableCell className={classes.cell}>Date sent</TableCell>
                    </TableRow>
                  </TableHead>
                  {paymentRecord && paymentRecord.communications && (
                    <TableBody data-cy="pr-details-comm-table">
                      {paymentRecord.communications.map(communication => {
                        if (communication) {
                          return (
                            <TableRow key={communication.id || uuid4()}>
                              <TableCell className={classes.cell}>{communication.phoneNumber || communication.email}</TableCell>
                              <TableCell className={classes.cell}>{communication.communicationType}</TableCell>
                              <TableCell className={classes.cell}>
                                {communication.requestTimestamp
                                  ? new Date(communication.requestTimestamp as string).toLocaleDateString()
                                  : ''}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return <></>;
                      })}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
      </>
    </Dialog>
  );
};
export default PaymentRequestDetails;
